import {
  type PassiveStat,
  POWERS,
  type PowerType,
  UPGRADES,
  type UpgradeDef,
} from "@roguelik/shared-sim";

/** Look up an augment (passive upgrade) definition by id. */
export function augmentDef(id: number): UpgradeDef | undefined {
  return UPGRADES[id];
}

/** The power a weapon-slot entry maps to (for damage-type / scaling hints). */
export function powerOf(powerId: number): PowerType | undefined {
  return POWERS[powerId];
}

const PCT_STATS = new Set<PassiveStat>([
  "moveMul",
  "damageMul",
  "cooldownMul",
  "magnetMul",
  "critChance",
  "lifesteal",
  "armor",
  "thorns",
  "dodgeChance",
  "projSpeedMul",
  "areaMul",
  "bossDmgMul",
  "executeFrac",
  "goldFind",
]);

const STAT_LABEL: Partial<Record<PassiveStat, string>> = {
  moveMul: "move speed",
  damageMul: "damage",
  cooldownMul: "cooldown",
  magnetMul: "pickup radius",
  critChance: "crit chance",
  critMul: "crit damage",
  lifesteal: "lifesteal",
  regenPerSec: "HP regen",
  armor: "damage reduction",
  thorns: "thorns",
  dodgeChance: "dodge",
  projSpeedMul: "projectile speed",
  areaMul: "area",
  bossDmgMul: "boss damage",
  executeFrac: "execute threshold",
  goldFind: "currency",
  maxHpBonus: "max HP",
  statik: "Statikk level",
  extraProjectiles: "projectiles",
  extraPierce: "pierce",
  homing: "homing",
};

const sign = (n: number): string => (n >= 0 ? "+" : "");

/**
 * Human-readable "what you currently gain from this augment" string for `count` stacks,
 * e.g. "+36% damage", "+60 max HP", "+3 projectiles". Returns null for unknown shapes.
 */
export function augmentGain(def: UpgradeDef, count: number): string | null {
  if (def.stat === undefined || def.amount === undefined) return null;
  const total = def.amount * count;
  const label = STAT_LABEL[def.stat] ?? def.stat;
  if (PCT_STATS.has(def.stat)) return `${sign(total)}${Math.round(total * 100)}% ${label}`;
  if (def.stat === "critMul") return `${sign(total)}${total.toFixed(2)}× ${label}`;
  if (def.stat === "regenPerSec") return `${sign(total)}${total.toFixed(1)}/s ${label}`;
  if (def.stat === "maxHpBonus") return `${sign(total)}${Math.round(total)} ${label}`;
  if (def.stat === "homing") return "homing projectiles";
  // Flat integer counters (statik / extraProjectiles / extraPierce).
  return `${sign(total)}${Math.round(total)} ${label}`;
}
